import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useTranslation, Trans } from "react-i18next";
import { useForm, useFieldArray } from "react-hook-form";
import type { Control, FieldErrors, UseFormRegister } from "react-hook-form";
import * as z from "zod/v4";
import { customDetailsEntryParser } from "../../../common/jsonRpcTypes";
import type { BookingOptions, BookingTag, CountryConfig, Rewards, VenueConfig } from "../../../common/jsonRpcTypes";
import type { AffiliateData } from "../hooks/useBookingCreate";
import {
  formatCurrency,
  formatDate,
  formatTimeOfDay,
  resolveCountryCode,
  resolveCurrencyCode,
} from "../../../utils/countryLocaleMap";
import { renderRewardsBalanceMessage } from "./rewardsBalanceMessage";
import { isRewardsEligible } from "../../utils/rewardsEligibility";
import PrimaryButton from "../buttons/primaryButton";
import { useInFlight } from "../hooks/useInFlight";
import { CTA_FULL_WIDTH, CTA_SHAPE } from "../buttons/ctaClassNames";
import TextInput from "../inputs/textInput";
import PhoneInput from "../inputs/phoneInput";
import TextareaInput from "../inputs/textareaInput";
import CheckboxInput from "../inputs/checkboxInput";
import DropdownInput from "../inputs/dropdownInput";
import { RequiredIndicator } from "../inputs/requiredIndicator";
import AlertBanner from "../shared/AlertBanner";
import { Alert } from "../ui/Alert";
import SlowRequestNotice from "../shared/SlowRequestNotice";
import { Clock, InfoCircle, PersonFill } from "../icons";
import { isBookingWithin24Hours } from "../../utils/bookingRules";
import { getPreorderAndPaymentMessage } from "../../utils/paymentMessaging";
import { Star } from "lucide-react";
import { useAccountDetection } from "../hooks/useAccountDetection";
import type { LoggedInUser } from "../hooks/useAccountDetection";
import { useAuthComponents } from "../hooks/useAuthComponents";
import DmnSignIn from "../shared/DmnSignIn";
import OffersSelector from "./OffersSelector";
import type { Offer } from "./OffersSelector";
import { Modal } from "../ui/Modal";
import { useGuestCheckoutSettings, useUsesDesign } from "../context/GuestCheckoutSettingsContext";
import { DEFAULT_DATE_FIELD_ORDER, getDateFieldOrder, getShortMonthNames } from "../../utils/localeDateFields";
import type { DateField } from "../../utils/localeDateFields";
import { useWidgetMaximised } from "../context/WidgetMaximisedContext";
import { useFormattingLocale } from "../hooks/useWidgetLocale";
import { getPhoneExample, isValidPhoneValue } from "../../utils/phoneNumber";
import { applyTagPrefill, parseIsoDateToDobFields } from "./bookingUrlParams";
import type { BookingPrefill, TagValue } from "./bookingUrlParams";

function sanitizeConsentHtml(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = DOMPurify.sanitize(html);

  for (const link of template.content.querySelectorAll("a[href]")) {
    const href = link.getAttribute("href");
    if (!href) continue;

    try {
      const url = new URL(href, "https://guest-checkout.invalid");
      if (url.protocol === "http:") {
        link.removeAttribute("href");
      } else if (url.protocol === "https:" && url.hostname !== "guest-checkout.invalid") {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      }
    } catch {
      link.removeAttribute("href");
    }
  }

  return template.innerHTML;
}

interface CustomerDetailsFormProps {
  readonly initialValues?: CustomerFormValues | null;
  readonly prefill?: BookingPrefill;
  readonly onSaveFormState?: (values: CustomerFormValues) => void;
  readonly bookingOptions: BookingOptions | null;
  readonly venueConfig: VenueConfig | null;
  readonly countryConfig: CountryConfig | null;
  readonly source: string;
  readonly numPeople: number;
  readonly selectedBookingTypeId: string | null;
  readonly onSubmit: (data: CustomerFormSubmitData) => void | Promise<void>;
  /** Pre-authenticated user returned by the /createBooking API when the guest is already logged in. */
  readonly initialLoggedInUser?: LoggedInUser | null;
  /** Rewards data returned by the /createBooking API (points earned for this booking). */
  readonly rewards?: Rewards | null;
  /** Affiliate id, when present. Affiliate bookings never earn reward points. */
  readonly affiliateId?: string | null;
  /** Name of the selected booking type. Concierge bookings never earn reward points. */
  readonly bookingTypeName?: string | null;
  /** Whether the booking is being submitted as an enquiry. */
  readonly isEnquiry?: boolean;
  /** Booking date (YYYY-MM-DD) for the enquiry alert banner. */
  readonly bookingDate?: string | null;
  /** Booking time (HH:mm) for the enquiry alert banner. */
  readonly bookingTime?: string | null;
  /**
   * Whether the customer details step is the final step before the booking is
   * created. When `false`, a further step (pre-order or payment) follows, so the
   * submit button reads "Next" rather than "Confirm Booking"/"Submit Enquiry".
   */
  readonly isFinalStep?: boolean;
  /** Available offers for this booking. */
  readonly offers?: readonly Offer[];
  /** Currently selected offer ID (null means "No thanks"). */
  readonly selectedOffer?: string | null;
  /** Callback when the user selects or deselects an offer. */
  readonly onSelectOffer?: (offerId: string | null) => void;
  /** Available packages for this booking. */
  readonly packages?: ReadonlyArray<{ id: string; label: string }>;
  /** Currently selected package ID (null means none selected). */
  readonly selectedPackage?: string | null;
  /** Callback when the user selects or deselects a package. */
  readonly onSelectPackage?: (packageId: string | null) => void;
  /** Affiliate data returned by the DMN /book response. */
  readonly affiliate?: AffiliateData | null;
  /** Locale override from URL query parameter (takes precedence over derived locale). */
  readonly locale?: string;
  /** The pending booking this form belongs to, used to link login/logout to the booking server-side. */
  readonly pendingBookingId?: string | null;
  /** Called after a successful login so the parent can re-sync the pending booking with the now-authenticated user. */
  readonly onAccountLinked?: () => void;
  /** Called after logout so the parent can forget the pre-authenticated user from the initial pending booking fetch. */
  readonly onAccountUnlinked?: () => void;
  /**
   * Hides the primary submit button — set when the booking's outcome is already
   * unknown (a timed-out `/submitBooking`), so the guest cannot submit again.
   */
  readonly submitHidden?: boolean;
}

/** Data shape passed to the parent on form submission. */
export interface CustomerFormSubmitData {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  notes: string;
  dob?: string;
  policy_confirm: boolean;
  newsletter_signup: boolean;
  /**
   * No dedicated partner-permission checkbox is collected by this form (see
   * buildSubmitData) — omitted (undefined) rather than false so Collins API
   * treats it as "not provided" and doesn't overwrite an existing consent
   * record for the venue group.
   */
  partner_data_permission?: boolean;
  marketing_preferences: Record<string, boolean>;
  tags: Record<string, TagValue>;
  group_members: GroupMember[];
  /** Whether the user was logged in (pre-authenticated or runtime login) when the form was submitted. */
  isLoggedIn: boolean;
  /** Whether the user has opted in to affiliate data permission. */
  affiliate_data_permission?: boolean;
  /** Legacy venue custom field value. */
  custom_field_value?: string;
  /** Legacy venue custom field label. */
  custom_field_label?: string;
}

interface GroupMember {
  first_name: string;
  last_name: string;
  gender?: string;
  email?: string;
  phone?: string;
}

/** Internal form values managed by react-hook-form. */
export interface CustomerFormValues {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  specialRequests: string;
  policyConfirm: boolean;
  newsletterSignup: boolean;
  affiliateDataPermission: boolean;
  marketingPreferences: Record<string, boolean>;
  tags: Record<string, TagValue>;
  groupMembers: GroupMember[];
  customFieldValue: string;
}

interface CustomerFormDefaultValuesParams {
  readonly prefill?: BookingPrefill;
  readonly defaultPhonePrefix: string;
  readonly showDob: boolean;
  readonly showNotes: boolean;
  readonly hasCustomField: boolean;
  readonly source: CustomerDetailsFormProps["source"];
  readonly newsletterSignup: boolean;
  readonly defaultMarketingValues: CustomerFormValues["marketingPreferences"];
  readonly filteredTags: readonly BookingTag[];
}

function createCustomerFormDefaultValues({
  prefill,
  defaultPhonePrefix,
  showDob,
  showNotes,
  hasCustomField,
  source,
  newsletterSignup,
  defaultMarketingValues,
  filteredTags,
}: CustomerFormDefaultValuesParams): CustomerFormValues {
  return {
    email: prefill?.email ?? "",
    firstName: prefill?.firstName ?? "",
    lastName: prefill?.lastName ?? "",
    phone: prefill?.phone ?? defaultPhonePrefix,
    dobDay: showDob ? (prefill?.dob?.day ?? "") : "",
    dobMonth: showDob ? (prefill?.dob?.month ?? "") : "",
    dobYear: showDob ? (prefill?.dob?.year ?? "") : "",
    specialRequests: showNotes ? (prefill?.notes ?? "") : "",
    customFieldValue: hasCustomField ? (prefill?.customFieldValue ?? "") : "",
    policyConfirm: false,
    newsletterSignup: source !== "partner" && newsletterSignup,
    affiliateDataPermission: false,
    marketingPreferences: defaultMarketingValues,
    tags: applyTagPrefill(prefill?.tags, filteredTags),
    groupMembers: [],
  };
}

const EMAIL_PATTERN = /^[^\s@]+@([^\s@.]+\.)+[^\s@.]+$/;

/**
 * Temporarily hides the DesignMyNight social sign-in widget (`<dmn-sign-in>`),
 * leaving email and password as the only way for a guest to sign in. The
 * integration itself is complete and stays in place ready for release — set
 * this to `false` to show the widget again.
 *
 * Typed as `boolean` rather than inferred as `true` so the surrounding
 * conditions stay meaningful expressions rather than dead branches.
 */
const HIDE_SOCIAL_SIGN_IN: boolean = true;

/** Filter booking tags by selected type, excluding archived tags. */
function filterBookingTags(bookingTags: BookingTag[] | undefined, selectedBookingTypeId: string | null): BookingTag[] {
  if (!bookingTags || !selectedBookingTypeId) return [];
  return bookingTags.filter((tag) => {
    if (tag.archived_at !== null && tag.archived_at !== undefined) return false;
    if (tag.booking_types && tag.booking_types.length > 0) {
      return tag.booking_types.includes(selectedBookingTypeId);
    }
    return true;
  });
}

const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1).padStart(2, "0"),
  label: String(i + 1),
}));

const MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function buildYearOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear();
  const options: { value: string; label: string }[] = [];
  for (let y = currentYear; y >= currentYear - 120; y--) {
    options.push({ value: String(y), label: String(y) });
  }
  return options;
}

const YEAR_OPTIONS = buildYearOptions();

interface DobFieldsetProps {
  readonly dobRequired: boolean;
  readonly register: UseFormRegister<CustomerFormValues>;
  readonly errors: FieldErrors<CustomerFormValues>;
  readonly t: ReturnType<typeof useTranslation>["t"];
  readonly onFieldChange?: (field: "dobDay" | "dobMonth" | "dobYear") => void;
  /** The guest's browser (or venue fallback) locale; unused on a legacy surface. */
  readonly formattingLocale?: string;
}

function DobFieldset({
  dobRequired,
  register,
  errors,
  t,
  onFieldChange,
  formattingLocale: locale,
}: DobFieldsetProps): React.ReactElement {
  const usesDesign = useUsesDesign();
  const isMaximised = useWidgetMaximised();
  const dobValidation = dobRequired ? { required: t("dob_required", "Please enter your date of birth") } : {};
  const dobErrorMessage = errors.dobDay?.message ?? errors.dobMonth?.message ?? errors.dobYear?.message;
  const hasDobError = !!dobErrorMessage;
  const dobErrorId = hasDobError ? "dob-error" : undefined;

  /**
   * Three pickers across one row leave no room for "Day"/"Month"/"Year" beside
   * a chevron, so on a design surface they are abbreviated and set above the
   * chevron rather than next to it (design-booking.dob-compact). Each select
   * keeps its full screen-reader label.
   *
   * The widget's maximised panel is the exception: it has the width for the
   * shared single-line controls, so it keeps the full labels.
   */
  const useCompactPlaceholders = usesDesign && !isMaximised;

  /**
   * A design surface orders the pickers the way its locale writes a date, so a
   * locale that puts month or year first (en-US, ja, ...) gets them in that
   * order. A surface still on the legacy look keeps day/month/year.
   *
   * Which locale that is comes from `useFormattingLocale`, and differs by
   * surface: the widget has no locale attribute so it follows the guest's
   * browser, while a hosted route follows the app's `locale` setting, falling
   * back to the venue's country.
   */
  const fieldOrder = useMemo(
    () => (usesDesign ? getDateFieldOrder(locale) : DEFAULT_DATE_FIELD_ORDER),
    [usesDesign, locale],
  );

  /**
   * A full month name overflows a compact picker, so it shows the locale's
   * abbreviation ("Sept") instead.
   */
  const monthOptions = useMemo(() => {
    if (!useCompactPlaceholders) return MONTH_OPTIONS;
    const shortNames = getShortMonthNames(locale);
    if (!shortNames) return MONTH_OPTIONS;
    return MONTH_OPTIONS.map((option, index) => ({ value: option.value, label: shortNames[index] }));
  }, [useCompactPlaceholders, locale]);

  const fields: Record<DateField, React.ReactElement> = {
    day: (
      <DropdownInput
        key="dobDay"
        id="dobDay"
        name="dobDay"
        label={<span className="gcw:sr-only">{t("dob_day", "Day")}</span>}
        options={DAY_OPTIONS}
        placeholder={useCompactPlaceholders ? "DD" : t("dob_day", "Day")}
        register={register}
        validation={dobValidation}
        error={errors.dobDay}
        hideErrorMessage
        className="gcw:flex-[2]"
        ariaDescribedBy={dobErrorId}
        onChange={() => onFieldChange?.("dobDay")}
      />
    ),
    month: (
      <DropdownInput
        key="dobMonth"
        id="dobMonth"
        name="dobMonth"
        label={<span className="gcw:sr-only">{t("dob_month", "Month")}</span>}
        options={monthOptions}
        placeholder={useCompactPlaceholders ? "MM" : t("dob_month", "Month")}
        register={register}
        validation={dobValidation}
        error={errors.dobMonth}
        hideErrorMessage
        className="gcw:flex-[3]"
        ariaDescribedBy={dobErrorId}
        onChange={() => onFieldChange?.("dobMonth")}
      />
    ),
    year: (
      <DropdownInput
        key="dobYear"
        id="dobYear"
        name="dobYear"
        label={<span className="gcw:sr-only">{t("dob_year", "Year")}</span>}
        options={YEAR_OPTIONS}
        placeholder={useCompactPlaceholders ? "YYYY" : t("dob_year", "Year")}
        register={register}
        validation={dobValidation}
        error={errors.dobYear}
        hideErrorMessage
        className="gcw:flex-[2]"
        ariaDescribedBy={dobErrorId}
        onChange={() => onFieldChange?.("dobYear")}
      />
    ),
  };

  return (
    <div className="gcw:w-full gcw:@md:w-1/2 gcw:mt-bs-3 gcw:@md:mt-0">
      <fieldset>
        <legend className="gcw:font-semibold gcw:text-body gcw:text-text-primary">
          {t("booking_dob", "Date of Birth")}
          {dobRequired && <span className="gcw:text-text-primary"> * </span>}
        </legend>
        <div className="booking-dob-fields gcw:flex gcw:gap-2">{fieldOrder.map((field) => fields[field])}</div>
        {hasDobError && (
          <div id="dob-error" className="gcw:text-error gcw:text-md gcw:mt-1" role="alert">
            {dobErrorMessage}
          </div>
        )}
      </fieldset>
    </div>
  );
}

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

interface GuestExtraFieldsProps {
  readonly idx: number;
  readonly guestGenderRequired: boolean;
  readonly guestEmailRequired: boolean;
  readonly guestPhoneRequired: boolean;
  readonly defaultPhonePrefix: string;
  readonly register: UseFormRegister<CustomerFormValues>;
  readonly errors: FieldErrors<CustomerFormValues>;
  readonly t: ReturnType<typeof useTranslation>["t"];
}

/** Renders the conditional gender / email / phone fields for a single guest row. */
function GuestExtraFields({
  idx,
  guestGenderRequired,
  guestEmailRequired,
  guestPhoneRequired,
  defaultPhonePrefix,
  register,
  errors,
  t,
}: GuestExtraFieldsProps): React.ReactElement {
  const emailValidationRule = {
    pattern: {
      value: EMAIL_PATTERN,
      message: t("invalid_email", "Please check that you have entered a valid email address"),
    },
  };

  return (
    <>
      {guestGenderRequired && guestEmailRequired && (
        <div className="gcw:flex gcw:flex-col gcw:@md:flex-row gcw:gap-2 gcw:@md:gap-x-bs-3">
          <DropdownInput
            id={`guest-${String(idx)}-gender`}
            name={`groupMembers.${idx}.gender`}
            label={t("gender", "Gender")}
            options={GENDER_OPTIONS}
            placeholder={t("select_gender", "Select...")}
            className="gcw:w-full gcw:@md:w-1/2"
            labelClassName="gcw:text-body gcw:text-text-primary gcw:mb-1"
            register={register}
            error={errors.groupMembers?.[idx]?.gender}
          />
          <TextInput
            id={`guest-${String(idx)}-email`}
            name={`groupMembers.${idx}.email`}
            label={t("email", "Email")}
            type="email"
            placeholder={t("email", "Email")}
            className="gcw:w-full gcw:@md:w-1/2"
            labelClassName="gcw:text-body gcw:text-text-primary gcw:mb-1"
            register={register}
            error={errors.groupMembers?.[idx]?.email}
            validation={emailValidationRule}
          />
        </div>
      )}

      {guestGenderRequired && !guestEmailRequired && (
        <DropdownInput
          id={`guest-${String(idx)}-gender`}
          name={`groupMembers.${idx}.gender`}
          label={t("gender", "Gender")}
          options={GENDER_OPTIONS}
          placeholder={t("select_gender", "Select...")}
          className="gcw:w-full gcw:@md:w-1/2"
          labelClassName="gcw:text-body gcw:text-text-primary gcw:mb-1"
          register={register}
          error={errors.groupMembers?.[idx]?.gender}
        />
      )}

      {!guestGenderRequired && guestEmailRequired && guestPhoneRequired && (
        <div className="gcw:flex gcw:flex-col gcw:@md:flex-row gcw:gap-2 gcw:@md:gap-x-bs-3">
          <TextInput
            id={`guest-${String(idx)}-email`}
            name={`groupMembers.${idx}.email`}
            label={t("email", "Email")}
            type="email"
            placeholder={t("email", "Email")}
            className="gcw:w-full gcw:@md:w-1/2"
            labelClassName="gcw:text-body gcw:text-text-primary gcw:mb-1"
            register={register}
            error={errors.groupMembers?.[idx]?.email}
            validation={emailValidationRule}
          />
          <TextInput
            id={`guest-${String(idx)}-phone`}
            name={`groupMembers.${idx}.phone`}
            label={t("mobile_phone", "Mobile Number")}
            type="tel"
            placeholder={defaultPhonePrefix || t("mobile_phone", "Mobile Number")}
            className="gcw:w-full gcw:@md:w-1/2"
            labelClassName="gcw:text-body gcw:text-text-primary gcw:mb-1"
            register={register}
            error={errors.groupMembers?.[idx]?.phone}
          />
        </div>
      )}

      {!guestGenderRequired && guestEmailRequired && !guestPhoneRequired && (
        <TextInput
          id={`guest-${String(idx)}-email`}
          name={`groupMembers.${idx}.email`}
          label={t("email", "Email")}
          type="email"
          placeholder={t("email", "Email")}
          className="gcw:w-full gcw:@md:w-1/2"
          labelClassName="gcw:text-body gcw:text-text-primary gcw:mb-1"
          register={register}
          error={errors.groupMembers?.[idx]?.email}
          validation={emailValidationRule}
        />
      )}

      {guestPhoneRequired && (guestGenderRequired || !guestEmailRequired) && (
        <TextInput
          id={`guest-${String(idx)}-phone`}
          name={`groupMembers.${idx}.phone`}
          label={t("mobile_phone", "Mobile Number")}
          type="tel"
          placeholder={defaultPhonePrefix || t("mobile_phone", "Mobile Number")}
          className="gcw:w-full gcw:@md:w-1/2"
          labelClassName="gcw:text-body gcw:text-text-primary gcw:mb-1"
          register={register}
          error={errors.groupMembers?.[idx]?.phone}
        />
      )}
    </>
  );
}

/** Build a default group member object with optional fields based on config. */
function buildDefaultGroupMember(
  guestGenderRequired: boolean,
  guestEmailRequired: boolean,
  guestPhoneRequired: boolean,
): GroupMember {
  return {
    first_name: "",
    last_name: "",
    ...(guestGenderRequired ? { gender: "" } : {}),
    ...(guestEmailRequired ? { email: "" } : {}),
    ...(guestPhoneRequired ? { phone: "" } : {}),
  };
}

/**
 * Extract a Date from a Collins date field, which may be a plain string
 * or a PHP-style date object `{ date, timezone_type, timezone }`.
 */
const phpDateObjectParser = z.object({ date: z.string() });
const mongoDateObjectParser = z.object({ $date: z.string() });

export function extractDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") return new Date(value);
  const phpParsed = phpDateObjectParser.safeParse(value);
  if (phpParsed.success) return new Date(phpParsed.data.date);
  const mongoParsed = mongoDateObjectParser.safeParse(value);
  if (mongoParsed.success) return new Date(mongoParsed.data.$date);
  return null;
}

/** Check whether a marketing preference is currently opted in, matching DMN's isPreferenceOptedIn logic. */
export function isPreferenceOptedIn(pref: { opt_in_date?: unknown; opt_out_date?: unknown }): boolean {
  const optInDate = extractDate(pref.opt_in_date);
  if (!optInDate || Number.isNaN(optInDate.getTime())) return false;
  const optOutDate = extractDate(pref.opt_out_date);
  if (!optOutDate || Number.isNaN(optOutDate.getTime())) return true;
  return optInDate > optOutDate;
}

interface CustomDetailsEntry {
  owner_id?: string;
  marketing_preferences?: Array<{
    id: string;
    opt_in_date?: unknown;
    opt_out_date?: unknown;
  }>;
}

/** Resolve the custom_details array from whichever user source is available. */
type CustomDetailsArray = CustomDetailsEntry[];

/**
 * Normalise custom_details into an array.  The Collins API may return either
 * a single object `{ owner_id, marketing_preferences }` or an array of such
 * objects depending on the endpoint.  This helper ensures downstream code can
 * always call `.find()` / `.filter()` safely.
 */
const customDetailsArrayParser = z.array(customDetailsEntryParser);

export function normalizeCustomDetails(raw: unknown): CustomDetailsArray | undefined {
  if (!raw) return undefined;
  const arrayResult = customDetailsArrayParser.safeParse(raw);
  if (arrayResult.success) return arrayResult.data;
  const singleResult = customDetailsEntryParser.safeParse(raw);
  if (singleResult.success) return [singleResult.data];
  return undefined;
}

export function resolveCustomDetails(
  initialUser: LoggedInUser | null | undefined,
  loggedInUser: LoggedInUser | null,
): CustomDetailsArray | undefined {
  if (loggedInUser) return normalizeCustomDetails(loggedInUser.custom_details);
  return normalizeCustomDetails(initialUser?.custom_details);
}

/**
 * Filter marketing preferences based on the user's custom_details, matching
 * DMN's setDefaultMarketingPreferences logic from bookController.js.
 * Preferences the user has already opted into are hidden from the form.
 */
export function filterMarketingPreferences(
  allPrefs: Array<{ id: string; description: string }>,
  customDetails: CustomDetailsArray | null | undefined,
  venueGroupId: string | undefined,
): Array<{ id: string; description: string }> {
  if (!customDetails || !venueGroupId) return allPrefs;

  const matchingDetails = customDetails.find((cd) => cd.owner_id === venueGroupId);
  const customerPrefs = matchingDetails?.marketing_preferences ?? [];

  if (customerPrefs.length === 0) return allPrefs;

  const optedInIds = customerPrefs.filter(isPreferenceOptedIn).map((p) => p.id);

  return allPrefs.filter((pref) => !optedInIds.includes(pref.id));
}

/**
 * Build the initial checkbox values for the displayed marketing preferences.
 * When the user is logged in, has custom_details and defaultOptIn is false,
 * preferences the user has explicitly opted out of are initialised as
 * unchecked. When defaultOptIn is true the opt-out override is skipped so
 * the preference is pre-checked by default.
 */
export function buildDefaultMarketingValues(
  filteredPrefs: ReadonlyArray<{ id: string }>,
  customDetails: CustomDetailsArray | null | undefined,
  venueGroupId: string | undefined,
  defaultOptIn: boolean,
): Record<string, boolean> {
  if (!customDetails || !venueGroupId) {
    return Object.fromEntries(filteredPrefs.map((pref) => [pref.id, defaultOptIn]));
  }
  const matchingDetails = customDetails.find((cd) => cd.owner_id === venueGroupId);
  const customerPrefs = matchingDetails?.marketing_preferences ?? [];
  return Object.fromEntries(
    filteredPrefs.map((pref) => {
      const customerPref = customerPrefs.find((p) => p.id === pref.id);
      // If the customer has a valid opt-out date and is not currently opted in,
      // respect the explicit opt-out by initialising as unchecked.
      if (
        !defaultOptIn &&
        customerPref &&
        extractDate(customerPref.opt_out_date) &&
        !isPreferenceOptedIn(customerPref)
      ) {
        return [pref.id, false];
      }
      return [pref.id, defaultOptIn];
    }),
  );
}

interface BuildSubmitDataParams {
  readonly data: CustomerFormValues;
  readonly showDob: boolean;
  readonly showGuestList: boolean;
  readonly showNewsletter: boolean;
  readonly displayedMarketingPrefs: Array<{ id: string }>;
  readonly defaultOptIn: boolean;
  readonly filteredTags: BookingTag[];
  readonly isLoggedIn: boolean;
  readonly customFieldLabel?: string;
}

/** Transform form values into the submission payload. */
/**
 * Build the marketing_preferences payload matching DMN's
 * getSelectedMarketingPreferenceIds logic.  Only displayed (not already
 * opted-in) preferences are considered.
 */
export function buildMarketingPreferences(
  displayedPrefs: ReadonlyArray<{ id: string }>,
  formValues: Record<string, boolean>,
  defaultOptIn: boolean,
): Record<string, boolean> {
  const initialState: Record<string, boolean> = {};
  const currentState: Record<string, boolean> = {};

  for (const pref of displayedPrefs) {
    initialState[pref.id] = defaultOptIn;
    // A preference with no form value has not been answered, so it falls back
    // to the venue default. Falling back to false instead would transmit an
    // opt-out the guest never made whenever the venue opts in by default.
    currentState[pref.id] = formValues[pref.id] ?? defaultOptIn;
  }

  // Opted in by default: every displayed preference is submitted, so unticking
  // a pre-ticked box is transmitted as an explicit false rather than omitted.
  if (defaultOptIn) {
    return currentState;
  }

  // Opted out by default: only preferences the guest actively changed are
  // submitted, so an untouched preference is left alone rather than opted out.
  const result: Record<string, boolean> = {};
  for (const key of Object.keys(initialState)) {
    if (initialState[key] !== currentState[key]) {
      result[key] = currentState[key];
    }
  }
  return result;
}

/** Map booking-tag form values to the shape expected by the submit API. */
export function buildTagsPayload(
  filteredTags: ReadonlyArray<{ id: string; type: string }>,
  formTags: Record<string, TagValue>,
): Record<string, string | string[] | boolean> {
  const tags: Record<string, string | string[] | boolean> = {};
  for (const tag of filteredTags) {
    const tagValue = formTags[tag.id];
    if (tagValue !== undefined && tagValue !== "") {
      tags[tag.id] = tag.type === "boolean" ? tagValue === "Yes" : tagValue;
    }
  }
  return tags;
}

function buildSubmitData({
  data,
  showDob,
  showGuestList,
  showNewsletter,
  displayedMarketingPrefs,
  defaultOptIn,
  filteredTags,
  isLoggedIn,
  customFieldLabel,
}: BuildSubmitDataParams): CustomerFormSubmitData {
  let dob: string | undefined;
  if (showDob && data.dobDay && data.dobMonth && data.dobYear) {
    dob = `${data.dobYear}-${data.dobMonth}-${data.dobDay}`;
  }

  return {
    email: data.email,
    first_name: data.firstName,
    last_name: data.lastName,
    phone: data.phone,
    notes: data.specialRequests,
    dob,
    policy_confirm: data.policyConfirm,
    newsletter_signup: showNewsletter ? data.newsletterSignup : false,
    // No dedicated partner-permission checkbox exists in the current booking form
    // (matching the live DMN booking form, which only collects newsletter_signup
    // and per-venue marketing preferences). Omit rather than send false, so
    // Collins API treats it as "not provided" and doesn't overwrite an
    // existing opt-in on the guest's record for this venue group.
    partner_data_permission: undefined,
    marketing_preferences: buildMarketingPreferences(displayedMarketingPrefs, data.marketingPreferences, defaultOptIn),
    tags: buildTagsPayload(filteredTags, data.tags),
    group_members: showGuestList ? data.groupMembers : [],
    isLoggedIn,
    affiliate_data_permission: data.affiliateDataPermission || undefined,
    custom_field_value: data.customFieldValue || undefined,
    custom_field_label: data.customFieldValue ? (customFieldLabel ?? undefined) : undefined,
  };
}

interface RenderBookingTagDeps {
  readonly register: UseFormRegister<CustomerFormValues>;
  readonly errors: FieldErrors<CustomerFormValues>;
  readonly t: ReturnType<typeof useTranslation>["t"];
}

/** Derive a locale string from countryConfig for newsletter visibility rules. */
function deriveLocale(countryConfig: CustomerDetailsFormProps["countryConfig"]): string | undefined {
  if (countryConfig?.country_code === "GB") return "en-GB";
  if (countryConfig?.country_code) return `en-${countryConfig.country_code}`;
  return undefined;
}

/** Derive visibility flags for newsletter and login sections. */
function deriveVisibilityFlags(params: {
  initialLoggedInUser: LoggedInUser | null | undefined;
  loggedInUser: LoggedInUser | null;
  emailExists: boolean;
  guestCheckout: boolean;
  source: string;
  newsletterSignup: boolean;
  locale: string | undefined;
  isWidget: boolean;
  /** Venue/affiliate setting that hides all login and social sign-in UI. */
  hideLoginSection: boolean;
}): { showNewsletter: boolean; showNewsletterTerms: boolean; showLoginSection: boolean } {
  const isLoggedIn = !!(params.initialLoggedInUser || params.loggedInUser);
  const newsletterLocaleAllowed = params.isWidget || !params.locale || params.locale === "en-GB";
  const showNewsletter =
    !isLoggedIn &&
    !params.emailExists &&
    params.source === "designmynight" &&
    params.newsletterSignup &&
    newsletterLocaleAllowed;
  return {
    showNewsletter,
    showNewsletterTerms: showNewsletter,
    showLoginSection: params.emailExists && !params.guestCheckout && !isLoggedIn && !params.hideLoginSection,
  };
}

/** Return the aria-describedby id when a field has an error, otherwise undefined. */
function errorAriaId(error: unknown, fieldName: string): string | undefined {
  if (error) return `${fieldName}-error`;
  return undefined;
}

/** Render a single booking tag based on its type. */
function renderBookingTag(tag: BookingTag, { register, errors, t }: RenderBookingTagDeps): React.ReactNode {
  const tagId = `tag-${tag.id}`;
  const isMandatory = tag.mandatory === true;
  const tagError = errors.tags?.[tag.id];
  const mandatoryRule = isMandatory
    ? { required: t("mandatory_tag_required", "Please complete all mandatory fields") }
    : {};

  const errorBlock = tagError ? (
    <div id={`${tagId}-error`} className="gcw:text-error gcw:text-md gcw:mt-1" role="alert">
      {typeof tagError === "object" && "message" in tagError ? (tagError as { message?: string }).message : undefined}
    </div>
  ) : null;

  const tagLabel = <span className="gcw:block gcw:text-body gcw:text-text-primary gcw:mb-1">{tag.label}</span>;

  switch (tag.type) {
    case "select":
      return (
        <DropdownInput
          key={tag.id}
          id={tagId}
          name={`tags.${tag.id}`}
          label={tagLabel}
          options={(tag.options ?? []).map((option) => ({ value: option, label: option }))}
          placeholder={t("booking_select_tag", { defaultValue: "Select..." })}
          required={isMandatory}
          register={register}
          validation={mandatoryRule}
          error={tagError}
          className="gcw:w-full"
          labelClassName="gcw:block gcw:text-body gcw:text-text-primary gcw:mb-1"
        />
      );

    case "multiselect":
      return (
        <fieldset key={tag.id} id={tagId} tabIndex={-1}>
          <legend className="gcw:block gcw:text-body gcw:text-text-primary gcw:mb-1">
            {tag.label}
            {isMandatory && <RequiredIndicator />}
          </legend>
          <div className="gcw:flex gcw:flex-col gcw:gap-2">
            {tag.options?.map((option) => (
              <CheckboxInput
                key={`${tag.id}-${option}`}
                id={`${tagId}-${option}`}
                name={`tags.${tag.id}`}
                value={option}
                label={<span className="gcw:text-body gcw:text-text-primary">{option}</span>}
                register={register}
                validation={mandatoryRule}
                invalid={Boolean(tagError)}
                ariaDescribedBy={tagError ? `${tagId}-error` : undefined}
              />
            ))}
          </div>
          {errorBlock}
        </fieldset>
      );

    case "text":
      return (
        <TextInput
          key={tag.id}
          id={tagId}
          name={`tags.${tag.id}`}
          label={tag.label}
          placeholder={tag.label}
          required={isMandatory}
          register={register}
          validation={mandatoryRule}
          error={tagError}
          className="gcw:w-full"
          labelClassName="gcw:block gcw:text-body gcw:text-text-primary gcw:mb-1"
        />
      );

    case "longtext":
      return (
        <TextareaInput
          key={tag.id}
          id={tagId}
          name={`tags.${tag.id}`}
          label={tagLabel}
          placeholder={tag.label}
          register={register}
          error={tagError}
          className="gcw:w-full"
        />
      );

    case "boolean":
      return (
        <DropdownInput
          key={tag.id}
          id={tagId}
          name={`tags.${tag.id}`}
          label={tagLabel}
          options={[
            { value: "Yes", label: t("booking_tag_yes", "Yes") },
            { value: "No", label: t("booking_tag_no", "No") },
          ]}
          placeholder={t("booking_select_tag", { defaultValue: "Select..." })}
          required={isMandatory}
          register={register}
          validation={mandatoryRule}
          error={tagError}
          className="gcw:w-full"
          labelClassName="gcw:flex gcw:flex-row gcw:text-body gcw:text-text-primary"
        />
      );

    default:
      return null;
  }
}

/**
 * Resolve the submit button's translation key and fallback text based on the
 * booking journey context.
 *
 * - When the details step is not the final step (a pre-order or payment step
 *   follows), the button advances the journey: "Next".
 * - When it is the final step and the booking is an enquiry: "Submit Enquiry".
 * - When it is the final step and the booking is confirmed directly:
 *   "Confirm Booking".
 */
function getSubmitButtonLabel(isFinalStep: boolean, isEnquiry: boolean): { key: string; fallback: string } {
  if (!isFinalStep) {
    return { key: "booking_next", fallback: "Next" };
  }
  if (isEnquiry) {
    return { key: "booking_submit_enquiry", fallback: "Submit Enquiry" };
  }
  return { key: "booking_confirm", fallback: "Confirm Booking" };
}

interface PhoneFieldProps {
  readonly usesDesign: boolean;
  readonly control: Control<CustomerFormValues>;
  readonly register: UseFormRegister<CustomerFormValues>;
  readonly error: FieldErrors<CustomerFormValues>["phone"];
  readonly defaultPhonePrefix: string;
  readonly countryCode: string | undefined;
  readonly formattingLocale: string | undefined;
  readonly requiredMessage: string;
  readonly ariaDescribedBy: string | undefined;
  readonly onCanonicalise: (value: string) => void;
  readonly onChange: () => void;
  readonly t: ReturnType<typeof useTranslation>["t"];
}

/**
 * Mobile-number field. A design surface states the number as the design's own
 * field: a dial-code combobox beside the national number, validated against the
 * country's own format. A surface still on the legacy look keeps the plain tel
 * input and its prefix placeholder.
 */
function PhoneField({
  usesDesign,
  control,
  register,
  error,
  defaultPhonePrefix,
  countryCode,
  formattingLocale,
  requiredMessage,
  ariaDescribedBy,
  onCanonicalise,
  onChange,
  t,
}: PhoneFieldProps): React.ReactElement {
  if (!usesDesign) {
    return (
      <TextInput
        id="phone"
        name="phone"
        label={t("mobile_phone", "Mobile Number")}
        type="tel"
        placeholder={defaultPhonePrefix || t("mobile_phone", "Mobile Number")}
        required
        className="gcw:w-full"
        labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
        register={register}
        error={error}
        validation={{ required: requiredMessage }}
        ariaDescribedBy={ariaDescribedBy}
        onChange={onChange}
      />
    );
  }

  return (
    <PhoneInput
      id="phone"
      name="phone"
      label={t("mobile_phone", "Mobile Number")}
      required
      className="gcw:w-full"
      labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
      control={control}
      defaultDialCode={defaultPhonePrefix}
      defaultCountry={countryCode}
      locale={formattingLocale}
      error={error}
      rules={{
        required: requiredMessage,
        validate: (value) => {
          if (typeof value === "string" && isValidPhoneValue(value)) return true;
          const example = getPhoneExample(typeof value === "string" ? value : "", countryCode);
          return example
            ? t("mobile_phone_invalid_example", "Please enter a valid mobile number, for example {{example}}", {
                example,
              })
            : t("mobile_phone_invalid", "Please enter a valid mobile number");
        },
      }}
      placeholder={t("mobile_phone", "Mobile Number")}
      ariaDescribedBy={ariaDescribedBy}
      onCanonicalise={onCanonicalise}
      onChange={onChange}
    />
  );
}

/**
 * CustomerDetailsForm -- Step 2 of the booking journey.
 *
 * Collects customer personal information including name, email,
 * phone, optional DOB, special requests, booking policy acceptance,
 * newsletter signup, marketing preferences, booking tags, and guest list.
 *
 * All conditional fields are driven by `bookingOptions` and `venueConfig`.
 * Client-side validation runs before submission via react-hook-form.
 */
const CustomerDetailsForm: React.FC<CustomerDetailsFormProps> = ({
  initialValues,
  prefill,
  onSaveFormState,
  bookingOptions,
  venueConfig,
  countryConfig,
  source,
  numPeople,
  selectedBookingTypeId,
  onSubmit,
  initialLoggedInUser,
  rewards,
  affiliateId,
  bookingTypeName,
  isEnquiry,
  bookingDate,
  bookingTime,
  isFinalStep = true,
  offers,
  selectedOffer,
  onSelectOffer,
  packages,
  selectedPackage,
  onSelectPackage,
  affiliate,
  locale: localeProp,
  pendingBookingId,
  onAccountLinked,
  onAccountUnlinked,
  submitHidden = false,
}) => {
  const { t } = useTranslation();
  const { isWidget } = useGuestCheckoutSettings();
  const usesDesign = useUsesDesign();

  // Controls the guest-names modal so the list can be edited without taking up
  // vertical space on the main "Your Details" screen.
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);

  // Submit button label adapts to the next step in the booking journey.
  const submitLabel = getSubmitButtonLabel(isFinalStep, isEnquiry === true);

  // --- Conditional visibility flags ---
  const showDob = bookingOptions?.dobRequired === true || bookingOptions?.dobHidden !== true;
  const dobRequired = bookingOptions?.dobRequired === true;
  const showNotes = !(bookingOptions?.specialRequestsHidden ?? false);
  const showPolicy = Boolean(
    DOMPurify.sanitize(bookingOptions?.bookingPolicy ?? "", { ALLOWED_TAGS: [] })
      .replace(/&nbsp;/gi, "")
      .trim(),
  );
  // Derive locale from countryConfig to match DMN's visibility rules.
  // Full newsletter visibility logic (including !emailExists) is computed
  // after useAccountDetection — see the newsletterEligible block below.
  const locale = localeProp ?? deriveLocale(countryConfig);
  const formattingLocale = useFormattingLocale(countryConfig?.country_code, locale);

  // --- Guest list config from the selected booking type ---
  const selectedBookingType = useMemo(() => {
    if (!venueConfig?.bookingTypes || !selectedBookingTypeId) return null;
    return venueConfig.bookingTypes.find((bt) => bt.id === selectedBookingTypeId) ?? null;
  }, [venueConfig?.bookingTypes, selectedBookingTypeId]);

  const showGuestList = !!(selectedBookingType?.all_names_required && numPeople >= 1);
  const guestEmailRequired = selectedBookingType?.email_required ?? selectedBookingType?.all_emails_required ?? false;
  const guestPhoneRequired = selectedBookingType?.phone_required ?? selectedBookingType?.all_phone_required ?? false;
  const guestGenderRequired = selectedBookingType?.gender_required ?? false;

  // --- Booking tags: filter by selected type, exclude archived ---
  const filteredTags: BookingTag[] = useMemo(
    () => filterBookingTags(venueConfig?.bookingTags, selectedBookingTypeId),
    [venueConfig?.bookingTags, selectedBookingTypeId],
  );

  // --- Marketing preferences ---
  // Memoised because `?? []` would otherwise mint a new array on every render,
  // invalidating every downstream useMemo and re-running the default-seeding
  // effect continuously.
  const allMarketingPrefs = useMemo(() => venueConfig?.marketingPreferences ?? [], [venueConfig?.marketingPreferences]);
  const defaultOptIn = venueConfig?.marketing_default_opt_in ?? false;
  const venueGroupId = venueConfig?.venueGroup;

  // Placeholder — actual filtered list is computed after useAccountDetection
  // so it can react to both initialLoggedInUser and runtime loggedInUser.
  // We still need a stable default for the initial useForm call below.
  const initialCustomDetails = useMemo(
    () => normalizeCustomDetails(initialLoggedInUser?.custom_details),
    [initialLoggedInUser?.custom_details],
  );

  const initialFilteredPrefs = useMemo(
    () => filterMarketingPreferences(allMarketingPrefs, initialCustomDetails, venueGroupId),
    [allMarketingPrefs, initialCustomDetails, venueGroupId],
  );

  const defaultMarketingValues = useMemo(
    () => buildDefaultMarketingValues(initialFilteredPrefs, initialCustomDetails, venueGroupId, defaultOptIn),
    [initialFilteredPrefs, initialCustomDetails, venueGroupId, defaultOptIn],
  );

  // Newsletter default: checked for non-partner sources (visible only to guests)
  const newsletterSignup = bookingOptions?.newsletterSignup ?? false;

  const defaultPhonePrefix = countryConfig?.phone_prefix ?? "";

  // --- Form setup ---
  const passwordRef = useRef("");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    getValues,
    reset,
    clearErrors,
    trigger,
  } = useForm<CustomerFormValues>({
    defaultValues:
      initialValues ??
      createCustomerFormDefaultValues({
        prefill,
        defaultPhonePrefix,
        showDob,
        showNotes,
        hasCustomField: Boolean(bookingOptions?.customField),
        source,
        newsletterSignup,
        defaultMarketingValues,
        filteredTags,
      }),
  });

  /**
   * When the component remounts with saved initial values (e.g. after navigating
   * back), reset the form to restore all previously entered data.
   * react-hook-form's defaultValues only apply on the very first render,
   * so an explicit reset is needed when the component re-mounts with new values.
   */
  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  // Persist current field values when the form unmounts (e.g. navigating back
  // a step via browser history) so they can be restored on return. Values are
  // otherwise only saved on submit, which loses unsubmitted edits.
  const onSaveFormStateRef = useRef(onSaveFormState);
  useEffect(() => {
    onSaveFormStateRef.current = onSaveFormState;
  }, [onSaveFormState]);
  useEffect(() => {
    return () => {
      onSaveFormStateRef.current?.(getValues());
    };
  }, [getValues]);

  /**
   * When the component mounts with a pre-authenticated user from the pending
   * booking API response, seed the form fields with the user's details.
   * This only runs once on mount (initialLoggedInUser is stable from props).
   */
  const hasSeededFromInitialUserRef = useRef(false);

  useEffect(() => {
    if (hasSeededFromInitialUserRef.current || !initialLoggedInUser) return;
    hasSeededFromInitialUserRef.current = true;
    setValue("email", initialLoggedInUser.email);
    setValue("firstName", initialLoggedInUser.first_name);
    setValue("lastName", initialLoggedInUser.last_name);
    setValue("phone", initialLoggedInUser.phone ?? defaultPhonePrefix);

    // Populate DOB fields if dateOfBirth is available
    const dobFields = parseIsoDateToDobFields(initialLoggedInUser.dob);
    if (dobFields) {
      setValue("dobDay", dobFields.day, { shouldValidate: true });
      setValue("dobMonth", dobFields.month, { shouldValidate: true });
      setValue("dobYear", dobFields.year, { shouldValidate: true });
    }
  }, [initialLoggedInUser, setValue, defaultPhonePrefix]);

  // Clear DOB fields when initialLoggedInUser becomes null/undefined (logout scenario)
  useEffect(() => {
    if (!initialLoggedInUser && hasSeededFromInitialUserRef.current) {
      setValue("dobDay", "");
      setValue("dobMonth", "");
      setValue("dobYear", "");
      hasSeededFromInitialUserRef.current = false;
    }
  }, [initialLoggedInUser, setValue]);

  /**
   * react-hook-form only auto-revalidates a field on change once the form has
   * been submitted at least once. Some flows (e.g. selecting a suggested email
   * or populating a logged-in user's DOB) call setValue with shouldValidate
   * before the guest has submitted, so without this those errors would get
   * stuck even after the guest starts typing valid details again.
   */
  const revalidateIfErrored = useCallback(
    (field: "email" | "firstName" | "lastName" | "phone" | "dobDay" | "dobMonth" | "dobYear") => {
      if (errors[field]) {
        trigger(field);
      }
    },
    [errors, trigger],
  );

  // --- Login / account detection (extracted to custom hook) ---
  const accountDetectionCallbacks = useMemo(
    () => ({
      getEmail: () => getValues("email"),
      getPassword: () => passwordRef.current,
      getFirstName: () => getValues("firstName"),
      getLastName: () => getValues("lastName"),
      onLoginSuccess: (user: LoggedInUser) => {
        setValue("email", user.email);
        setValue("firstName", user.first_name);
        setValue("lastName", user.last_name);
        if (user.phone) {
          setValue("phone", user.phone);
        }

        // Populate DOB fields if dateOfBirth is available
        const dobFields = parseIsoDateToDobFields(user.dob);
        if (dobFields) {
          setValue("dobDay", dobFields.day, { shouldValidate: true });
          setValue("dobMonth", dobFields.month, { shouldValidate: true });
          setValue("dobYear", dobFields.year, { shouldValidate: true });
        } else {
          setValue("dobDay", "");
          setValue("dobMonth", "");
          setValue("dobYear", "");
        }

        // Remove any stale required-field errors left over from a previous logout
        clearErrors(["email", "firstName", "lastName", "phone"]);

        // Re-sync the pending booking now that the user is authenticated, so
        // the booking record on DMN ends up linked to the account even when
        // login happens mid-form (after the pending booking already exists).
        onAccountLinked?.();
      },
      onEmailPrefill: (email: string) => {
        setValue("email", email, { shouldValidate: true });
        clearErrors("email");
      },
      onLogout: () => {
        setValue("firstName", "");
        setValue("lastName", "");
        setValue("phone", defaultPhonePrefix);
        setValue("email", "");
        setValue("dobDay", "");
        setValue("dobMonth", "");
        setValue("dobYear", "");
        passwordRef.current = "";
        clearErrors(["email", "firstName", "lastName", "phone", "dobDay", "dobMonth", "dobYear"]);
        onAccountUnlinked?.();
      },
    }),
    [getValues, setValue, clearErrors, defaultPhonePrefix, onAccountUnlinked, onAccountLinked],
  );

  const {
    emailExists,
    guestCheckout,
    loginError,
    loginLoading,
    loggedInUser,
    suggestedEmail,
    handleEmailBlur,
    handleLogin,
    handleGuestContinue,
    handleLogout,
    clearSuggestedEmail,
    handleSocialLoginSuccess,
    handleSocialProfileIncomplete,
  } = useAccountDetection(accountDetectionCallbacks, initialLoggedInUser);

  // The single source of truth for "is this guest authenticated right now?" —
  // covers both a cookie-based session detected on load (initialLoggedInUser)
  // and a login performed during this form session (loggedInUser). Using this
  // consistently everywhere (read-only email, logout banner, login section
  // visibility) avoids the two states drifting out of sync.
  const currentUser = loggedInUser ?? initialLoggedInUser ?? null;

  const { ready: authComponentsReady, config: authComponentsConfig } = useAuthComponents();

  // Clear stale password when the UI switches between login and sign-up flows.
  // biome-ignore lint/correctness/useExhaustiveDependencies: emailExists intentionally triggers the reset
  useEffect(() => {
    passwordRef.current = "";
  }, [emailExists]);

  // Now that loggedInUser is available, compute the final filtered marketing
  // preferences using whichever user source has custom_details (runtime login
  // takes precedence over the initial /fetchPendingBookingId user).
  const activeCustomDetails = useMemo(
    () => resolveCustomDetails(initialLoggedInUser, loggedInUser),
    [initialLoggedInUser, loggedInUser],
  );
  const filteredMarketingPrefs = useMemo(
    () => filterMarketingPreferences(allMarketingPrefs, activeCustomDetails, venueGroupId),
    [allMarketingPrefs, activeCustomDetails, venueGroupId],
  );

  const { fields: guestFields, replace: replaceGuests } = useFieldArray({
    control,
    name: "groupMembers",
  });

  // Adjust guest list size when numPeople changes
  useEffect(() => {
    const currentMembers = getValues("groupMembers");
    if (!showGuestList) {
      if (currentMembers.length > 0) replaceGuests([]);
      return;
    }
    const targetCount = Math.max(0, numPeople);
    if (currentMembers.length === targetCount) return;
    const newMembers = currentMembers.slice(0, targetCount);
    while (newMembers.length < targetCount) {
      newMembers.push(buildDefaultGroupMember(guestGenderRequired, guestEmailRequired, guestPhoneRequired));
    }
    replaceGuests(newMembers);
  }, [numPeople, showGuestList, guestGenderRequired, guestEmailRequired, guestPhoneRequired, getValues, replaceGuests]);

  // Seed the venue's default opt-in value for each displayed marketing
  // preference, exactly once per preference.
  //
  // Seeding must be additive rather than a one-shot: the displayed set grows
  // and shrinks as the guest signs in, signs up or logs out (which changes
  // which preferences are already opted into), and `venueConfig` may not have
  // arrived on first render. Re-seeding a preference the guest has already
  // answered would silently re-tick a box they deliberately unticked, so a
  // preference is only ever seeded the first time it appears.
  //
  // Deliberately tracked in a ref rather than read back from form state.
  // `initialValues` changes identity on every submit (onSaveFormState runs
  // before onSubmit, and BookingForm awaits before changing stage), so the
  // reset effect above re-runs against a still-mounted form. reset() drops
  // keys absent from its payload out of getValues(), yet these checkboxes are
  // uncontrolled, so the DOM keeps the guest's answer and submit reads it back
  // from there. Seeding off getValues() would therefore see "undefined" for a
  // preference the guest had actually answered and overwrite it with the venue
  // default.
  const seededMarketingPrefs = useRef<Set<string>>(new Set(Object.keys(initialValues?.marketingPreferences ?? {})));

  useEffect(() => {
    const values = buildDefaultMarketingValues(filteredMarketingPrefs, activeCustomDetails, venueGroupId, defaultOptIn);
    for (const [prefId, value] of Object.entries(values)) {
      if (seededMarketingPrefs.current.has(prefId)) continue;
      seededMarketingPrefs.current.add(prefId);
      setValue(`marketingPreferences.${prefId}`, value);
    }
  }, [filteredMarketingPrefs, activeCustomDetails, venueGroupId, defaultOptIn, setValue]);

  // --- Validation rules ---
  const emailValidation = {
    required: t("email_required", "Please enter your email address"),
    pattern: {
      value: EMAIL_PATTERN,
      message: t("invalid_email", "Please check that you have entered a valid email address"),
    },
  };

  const firstNameValidation = {
    required: t("first_name_required", "Please enter your first name"),
  };

  const lastNameValidation = {
    required: t("last_name_required", "Please enter your last name"),
  };

  // --- Form submission ---
  const submitGuard = useInFlight();
  const onFormSubmit = async (data: CustomerFormValues) => {
    const submitData = buildSubmitData({
      data,
      showDob,
      showGuestList,
      showNewsletter,
      displayedMarketingPrefs: filteredMarketingPrefs,
      defaultOptIn,
      filteredTags,
      isLoggedIn: !!currentUser,
      customFieldLabel: bookingOptions?.customField ?? undefined,
    });
    onSaveFormState?.(data);
    await onSubmit(submitData);
  };
  // Disable before validating, so a second click or Enter cannot slip in while validation runs.
  const handleFormSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submitGuard.run(() => handleSubmit(onFormSubmit)(event));
  };

  // Newsletter visibility flags are computed after useAccountDetection so
  // they react to runtime login and emailExists state.
  // Newsletter is shown for non-logged-in users on designmynight source when
  // newsletterSignup is enabled; outside the widget it also requires an en-GB
  // (or unset) locale, while the widget ignores locale.
  const { showNewsletter, showNewsletterTerms, showLoginSection } = deriveVisibilityFlags({
    initialLoggedInUser,
    loggedInUser,
    emailExists,
    guestCheckout,
    source,
    newsletterSignup,
    locale,
    isWidget,
    hideLoginSection: affiliate?.hide_login_section === true,
  });
  const emailAriaDescribedBy = errorAriaId(errors.email, "email");
  const firstNameAriaDescribedBy = errorAriaId(errors.firstName, "firstName");
  const lastNameAriaDescribedBy = errorAriaId(errors.lastName, "lastName");
  const phoneAriaDescribedBy = errorAriaId(errors.phone, "phone");
  const loginAriaDescribedBy = errorAriaId(loginError, "login");
  const loginButtonText = loginLoading ? t("logging_in", "Logging in...") : t("booking_login", "Log In");

  const bookingTagDeps: RenderBookingTagDeps = useMemo(() => ({ register, errors, t }), [register, errors, t]);

  // The venue's own currency is authoritative — a venue may trade in a currency
  // that differs from its country's default (e.g. a GB venue charging in USD) —
  // so the country configuration only supplies the formatting locale.
  const currency = resolveCurrencyCode(venueConfig?.currency, countryConfig?.currency);
  const countryCode = resolveCountryCode(countryConfig?.country_code);

  // Context-aware pre-order/payment heads-up shown before the guest pre-orders or
  // pays, mirroring the v2 booking-form details stage. Derived purely from config.
  const preorderPaymentMessage = useMemo(() => {
    const preorderMenus = selectedBookingTypeId ? (bookingOptions?.preorders?.[selectedBookingTypeId] ?? []) : [];
    const selectedType = venueConfig?.bookingTypes.find((bt) => bt.id === selectedBookingTypeId);
    const depositRequired = bookingOptions?.depositRequired;
    const descriptor = getPreorderAndPaymentMessage({
      hasMenus: preorderMenus.length > 0,
      preorderIsRequired: preorderMenus.some((menu) => (menu.minimum_preorder_amount ?? 0) > 0),
      preorderPaymentRequired: selectedType?.customer_preorder_settings?.payment_required,
      depositType: depositRequired?.type ?? null,
      amount: formatCurrency(depositRequired?.total ?? 0, currency, countryCode),
      depositTakeWithPreorder: depositRequired?.takeWithPreorder,
    });
    if (!descriptor) return null;
    return t(descriptor.key, descriptor.defaultValue, descriptor.values);
  }, [bookingOptions, venueConfig, currency, countryCode, selectedBookingTypeId, t]);

  const preorderPaymentBanner = preorderPaymentMessage && (
    <AlertBanner
      variant="warning"
      icon={
        <span className="gcw:flex gcw:shrink-0 gcw:items-center gcw:justify-center gcw:me-2 gcw:leading-none">
          <InfoCircle color="#9a3412" size={16} aria-hidden="true" className="gcw:block" />
        </span>
      }
      className="info-alert gcw:mb-bs-3"
    >
      {preorderPaymentMessage}
    </AlertBanner>
  );

  // Points alert banner — shown only when Collins would actually award the points
  const pointsBanner = rewards && isRewardsEligible({ rewards, source, affiliateId, bookingTypeName }) && (
    <AlertBanner
      className="gcw:mb-bs-3 gcw:bg-light-purple gcw:border-0 gcw:text-text-dmn gcw:rounded-alert"
      contentClassName="gcw:gap-4"
      icon={
        <span className="gcw:flex gcw:shrink-0 gcw:items-center gcw:justify-center gcw:w-8 gcw:h-8 gcw:rounded-full gcw:bg-dark-purple gcw:leading-none">
          <Star size={16} color="#FFFFFF" fill="#FFFFFF" aria-hidden="true" className="gcw:block" />
        </span>
      }
    >
      <div>
        <Trans
          i18nKey={currentUser ? "booking_points_banner_logged_in" : "booking_points_banner"}
          defaults={
            currentUser
              ? "Earn <strong>{{points}} points ({{pointsValue}})</strong> with this booking!{{redeemMessage}}"
              : "Earn <strong>{{points}} points ({{pointsValue}})</strong> with this booking! Login or sign up after to claim.{{redeemMessage}}"
          }
          values={{
            points: rewards.points,
            pointsValue: formatCurrency(rewards.pointsValue, currency, countryCode),
            redeemMessage:
              rewards.redeemMinimumAmount != null && rewards.redeemMinimumValue != null
                ? t("booking_points_redeem_message", {
                    amount: rewards.redeemMinimumAmount,
                    value: formatCurrency(rewards.redeemMinimumValue, currency, countryCode),
                  })
                : "",
          }}
          components={{ strong: <strong /> }}
        />
        {renderRewardsBalanceMessage(rewards, currency, countryCode, t, !!currentUser, currentUser?.points_balance)}
      </div>
    </AlertBanner>
  );

  // Offers selector — shown when offers are available
  const offersBlock = offers && offers.length > 0 && onSelectOffer && (
    <OffersSelector offers={offers} selectedOffer={selectedOffer ?? null} onSelectOffer={onSelectOffer} />
  );

  return (
    <section id="gc-customer-details-section" aria-labelledby="customer-details-heading">
      <div className="gcw:flex gcw:items-center gcw:gap-4 gcw:mt-4 gcw:mb-bs-3">
        <h2
          id="customer-details-heading"
          className="gcw:text-2xl gcw:font-heading gcw:font-bold gcw:text-text-primary gcw:whitespace-nowrap gcw:shrink-0"
        >
          {t("booking_customer_details")}
        </h2>
        <hr className="gcw:border-t gcw:border-button-grey gcw:flex-1 gcw:min-w-0" />
      </div>

      {isEnquiry && bookingTypeName && bookingDate && bookingTime && (
        <div data-testid="enquiry-alert-banner">
          <AlertBanner variant="info" icon={<InfoCircle size={16} aria-hidden="true" />} className="gcw:mb-bs-3">
            {t("booking_enquiry_alert", {
              bookingType: bookingTypeName,
              numPeople,
              time: formatTimeOfDay(bookingTime, countryCode, isWidget ? formattingLocale : undefined),
              date: formatDate(bookingDate, countryCode, "long"),
            })}
          </AlertBanner>
        </div>
      )}

      {isEnquiry && bookingDate && bookingTime && isBookingWithin24Hours(bookingDate, bookingTime) && (
        <Alert
          variant="warning"
          data-testid="availability-time-warning"
          className="gcw:mb-bs-3 gcw:flex gcw:items-start gcw:gap-2"
        >
          <Clock size={16} className="gcw:mt-0.5 gcw:shrink-0 widget-panel-icon" aria-hidden="true" />
          <span>{t("booking_time_warning")}</span>
        </Alert>
      )}

      {!isWidget && preorderPaymentBanner}

      {!isWidget && pointsBanner}

      <form
        id="gc-customer-details-form"
        noValidate
        onSubmit={handleFormSubmit}
        aria-busy={submitGuard.isPending || undefined}
        className="gcw:flex gcw:flex-col gcw:gap-bs-3 customer-information"
      >
        {!isWidget && offersBlock}

        {/* Email + Password (side by side on desktop when account detected, stacked on mobile) */}
        <div
          className={`gcw:w-full gcw:flex gcw:flex-col${showLoginSection ? " gcw:@md:flex-row gcw:@md:gap-bs-3" : ""}`}
        >
          <div className={showLoginSection ? "gcw:w-full gcw:@md:w-1/2" : "gcw:w-full"}>
            <TextInput
              id="email"
              name="email"
              label={t("email", "Email")}
              type="email"
              placeholder={t("email", "Email")}
              required
              className="gcw:w-full"
              labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary gcw:mb-2"
              register={register}
              error={errors.email}
              validation={emailValidation}
              ariaDescribedBy={emailAriaDescribedBy}
              onBlur={handleEmailBlur}
              onFocus={clearSuggestedEmail}
              onChange={() => revalidateIfErrored("email")}
              readOnly={!!currentUser}
            />
            {suggestedEmail && (
              <div
                className="gcw:grid gcw:animate-reveal-line gcw:motion-reduce:animate-none"
                data-testid="email-suggestion"
              >
                <span className="gcw:min-h-0 gcw:overflow-hidden gcw:p-[3px] gcw:-m-[3px] gcw:text-body">
                  {t("email_did_you_mean", "Did you mean")}{" "}
                  <button
                    type="button"
                    className="gcw:text-body gcw:text-primary gcw:cursor-pointer gcw:underline gcw:font-semibold gcw:hover:text-primary/80 gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-1 gcw:rounded-sm"
                    onClick={() => {
                      setValue("email", suggestedEmail, { shouldValidate: true });
                      clearSuggestedEmail();
                      handleEmailBlur();
                    }}
                  >
                    {suggestedEmail}
                  </button>
                  {"?"}
                </span>
              </div>
            )}
          </div>
          {showLoginSection && (
            <TextInput
              id="login-password"
              name="login-password"
              label={t("password", "Password")}
              type="password"
              placeholder={t("password", "Password")}
              required
              className="gcw:w-full gcw:@md:w-1/2 gcw:mt-bs-3 gcw:@md:mt-0"
              labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary gcw:mb-2"
              onChange={(e) => {
                passwordRef.current = e.target.value;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleLogin();
                }
              }}
              ariaDescribedBy={loginAriaDescribedBy}
            />
          )}
        </div>

        {/* Logged-in user banner */}
        {currentUser && (
          <div className="gcw:w-full">
            <div className="gcw:flex gcw:flex-row gcw:text-body">
              <p>{t("not_you", "Not you?")}</p>
              <button
                type="button"
                onClick={handleLogout}
                className="gcw:ms-1 gcw:underline gcw:text-semibold gcw:font-bold gcw:text-dark-purple gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-1 gcw:rounded-sm gcw:hover:cursor-pointer"
              >
                {t("logout", "Log out")}
              </button>
            </div>
          </div>
        )}

        {/* Login section -- welcome message and actions */}
        {showLoginSection && (
          <section aria-label={t("login_section", "Account detected")} className="gcw:w-full">
            <p className="gcw:text-body gcw:text-dark-blue">
              {t("account_found", "Welcome back! We recognise this email, log in or continue as guest")}
            </p>
            {loginError && (
              <div id="login-error" className="gcw:mt-2">
                <AlertBanner variant="danger">
                  <span role="alert">{loginError}</span>
                </AlertBanner>
              </div>
            )}
            <div className="gcw:flex gcw:flex-col gcw:gap-4 gcw:mt-4">
              <div className="gcw:flex gcw:gap-4">
                <PrimaryButton
                  text={loginButtonText}
                  type="button"
                  onClick={handleLogin}
                  disabled={loginLoading}
                  loading={loginLoading}
                  className={`gcw:inline-flex gcw:flex-1 gcw:items-center gcw:justify-center gcw:bg-dark-purple gcw:text-white gcw:border gcw:border-dark-purple gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:hover:cursor-pointer ${CTA_SHAPE}`}
                />
                <button
                  type="button"
                  onClick={handleGuestContinue}
                  className={`gcw:inline-flex gcw:flex-1 gcw:items-center gcw:justify-center gcw:border gcw:border-button-grey gcw:bg-transparent gcw:text-text-policy gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary/5 gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:hover:cursor-pointer ${CTA_SHAPE}`}
                >
                  {t("booking_continue_guest", "Continue as Guest")}
                </button>
              </div>
              {!HIDE_SOCIAL_SIGN_IN && authComponentsReady && authComponentsConfig && (
                <DmnSignIn
                  authApiHost={authComponentsConfig.authApiUrl}
                  clientId={authComponentsConfig.clientId}
                  clientSecret={authComponentsConfig.clientSecret}
                  onLoginSuccess={handleSocialLoginSuccess}
                  onProfileIncomplete={handleSocialProfileIncomplete}
                />
              )}
            </div>
          </section>
        )}

        {/* Social login — shown when login section is NOT visible (email not recognised) */}
        {!HIDE_SOCIAL_SIGN_IN &&
          !showLoginSection &&
          !currentUser &&
          !guestCheckout &&
          affiliate?.hide_login_section !== true &&
          authComponentsReady &&
          authComponentsConfig && (
            <div className="gcw:w-full gcw:mt-4">
              <DmnSignIn
                authApiHost={authComponentsConfig.authApiUrl}
                clientId={authComponentsConfig.clientId}
                clientSecret={authComponentsConfig.clientSecret}
                onLoginSuccess={handleSocialLoginSuccess}
                onProfileIncomplete={handleSocialProfileIncomplete}
              />
            </div>
          )}

        {/* First Name + Last Name -- side by side on desktop, stacked on mobile */}
        <div className="gcw:w-full gcw:flex gcw:flex-col gcw:@md:flex-row gcw:@md:gap-bs-3">
          <div className="gcw:w-full gcw:@md:w-1/2">
            <TextInput
              id="firstName"
              name="firstName"
              label={t("first_name", "First Name")}
              placeholder={t("first_name", "First Name")}
              required
              className="gcw:w-full"
              labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
              register={register}
              error={errors.firstName}
              validation={firstNameValidation}
              ariaDescribedBy={firstNameAriaDescribedBy}
              onChange={() => revalidateIfErrored("firstName")}
            />
          </div>
          <div className="gcw:w-full gcw:@md:w-1/2 gcw:mt-bs-3 gcw:@md:mt-0">
            <TextInput
              id="lastName"
              name="lastName"
              label={t("last_name", "Last Name")}
              placeholder={t("last_name", "Last Name")}
              required
              className="gcw:w-full"
              labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
              register={register}
              error={errors.lastName}
              validation={lastNameValidation}
              ariaDescribedBy={lastNameAriaDescribedBy}
              onChange={() => revalidateIfErrored("lastName")}
            />
          </div>
        </div>

        {/* Mobile Number + Date of Birth -- side by side on desktop, stacked on mobile */}
        <div className="gcw:w-full gcw:flex gcw:flex-col gcw:@md:flex-row gcw:@md:gap-bs-3">
          <div className={`gcw:w-full ${showDob ? "gcw:@md:w-1/2" : ""}`}>
            <PhoneField
              usesDesign={usesDesign}
              control={control}
              register={register}
              error={errors.phone}
              defaultPhonePrefix={defaultPhonePrefix}
              countryCode={countryConfig?.country_code}
              formattingLocale={formattingLocale}
              requiredMessage={t("phone_required", "Please enter your phone number")}
              ariaDescribedBy={phoneAriaDescribedBy}
              onCanonicalise={(value) =>
                setValue("phone", value, { shouldDirty: false, shouldValidate: false, shouldTouch: false })
              }
              onChange={() => revalidateIfErrored("phone")}
              t={t}
            />
          </div>
          {showDob && (
            <DobFieldset
              dobRequired={dobRequired}
              register={register}
              errors={errors}
              t={t}
              onFieldChange={revalidateIfErrored}
              formattingLocale={formattingLocale}
            />
          )}
        </div>

        {/* Packages dropdown */}
        {packages && packages.length > 0 && (
          <DropdownInput
            id="booking-package"
            name="bookingPackage"
            label={t("interested_in_package", "Interested in a package?")}
            placeholder={t("booking_select_package", "Select a package...")}
            options={packages.map((pkg) => ({ value: pkg.id, label: pkg.label }))}
            value={selectedPackage ?? ""}
            onChange={(e) => onSelectPackage?.(e.target.value || null)}
            className="gcw:w-full"
            labelClassName="gcw:font-semibold gcw:text-body gcw:text-text-primary"
            selectClassName="container-inputs"
          />
        )}

        {/* Special Requests */}
        {showNotes && (
          <TextareaInput
            id="specialRequests"
            name="specialRequests"
            label={t("booking_special_requests", "Special Requests")}
            placeholder={t("booking_special_requests", "Special Requests")}
            register={register}
            className="gcw:w-full"
            error={errors.specialRequests}
          />
        )}

        {/* Legacy venue custom field */}
        {bookingOptions?.customField && (
          <TextInput
            id="custom-field-value"
            name="customFieldValue"
            label={bookingOptions.customField}
            register={register}
            className="gcw:w-full"
            placeholder={bookingOptions.customField}
            labelClassName="gcw:block gcw:text-body gcw:text-text-primary gcw:mb-1"
          />
        )}

        {/* Guest list — collapsed into a modal to save space on the details screen */}
        {showGuestList && guestFields.length > 0 && (
          <div className="gcw:w-full">
            <button
              type="button"
              onClick={() => setIsGuestModalOpen(true)}
              className="gcw:inline-flex gcw:w-full gcw:items-center gcw:justify-center gcw:gap-2 gcw:rounded-md gcw:border gcw:border-gray-300 gcw:bg-white gcw:px-bs-3 gcw:py-3 gcw:text-body gcw:font-medium gcw:text-text-primary gcw:transition-colors gcw:hover:border-primary gcw:hover:bg-gray-50 gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2"
              aria-haspopup="dialog"
              aria-expanded={isGuestModalOpen}
              data-testid="manage-guest-names-btn"
            >
              <PersonFill size={18} aria-hidden="true" />
              {t("booking_manage_guest_names", {
                count: guestFields.length,
                defaultValue: "Manage Guest Names ({{count}})",
              })}
            </button>

            <Modal
              show={isGuestModalOpen}
              onHide={() => setIsGuestModalOpen(false)}
              centered
              ariaLabel={t("booking_guest_list")}
              dialogClassName="guest-list-modal"
            >
              <Modal.Header closeButton className="gcw:px-bs-3 gcw:pt-bs-3 gcw:pb-2">
                <Modal.Title>
                  <h2 className="gcw:text-xl gcw:font-heading gcw:font-bold gcw:text-text-primary">
                    {t("booking_guest_list")}
                  </h2>
                </Modal.Title>
              </Modal.Header>

              <Modal.Body className="gcw:max-h-[60vh] gcw:overflow-y-auto gcw:px-bs-3">
                <fieldset className="gcw:w-full">
                  <legend className="gcw:sr-only">{t("booking_guest_list")}</legend>
                  <div className="gcw:flex gcw:flex-col gcw:gap-bs-3">
                    {guestFields.map((field, idx) => (
                      <div key={field.id} className="gcw:rounded-md gcw:border gcw:border-gray-200 gcw:p-bs-3">
                        <p className="gcw:font-semibold gcw:text-body gcw:text-text-primary gcw:mb-2">
                          {t("booking_guest_number", { number: idx + 1, defaultValue: `Guest ${String(idx + 1)}` })}
                        </p>
                        <div className="gcw:flex gcw:flex-col gcw:gap-2">
                          <div className="gcw:flex gcw:flex-col gcw:@md:flex-row gcw:gap-2 gcw:@md:gap-x-bs-3">
                            <TextInput
                              id={`guest-${String(idx)}-first-name`}
                              name={`groupMembers.${idx}.first_name`}
                              label={t("first_name", "First Name")}
                              placeholder={t("first_name", "First Name")}
                              className="gcw:w-full gcw:@md:w-1/2"
                              labelClassName="gcw:text-body gcw:text-text-primary gcw:mb-1"
                              register={register}
                              error={errors.groupMembers?.[idx]?.first_name}
                            />
                            <TextInput
                              id={`guest-${String(idx)}-last-name`}
                              name={`groupMembers.${idx}.last_name`}
                              label={t("last_name", "Last Name")}
                              placeholder={t("last_name", "Last Name")}
                              className="gcw:w-full gcw:@md:w-1/2"
                              labelClassName="gcw:text-body gcw:text-text-primary gcw:mb-1"
                              register={register}
                              error={errors.groupMembers?.[idx]?.last_name}
                            />
                          </div>

                          <GuestExtraFields
                            idx={idx}
                            guestGenderRequired={guestGenderRequired}
                            guestEmailRequired={guestEmailRequired}
                            guestPhoneRequired={guestPhoneRequired}
                            defaultPhonePrefix={defaultPhonePrefix}
                            register={register}
                            errors={errors}
                            t={t}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </Modal.Body>

              <Modal.Footer className="gcw:px-bs-3 gcw:pb-bs-3 gcw:mt-4">
                <PrimaryButton
                  text={t("booking_guest_names_done", "Done")}
                  type="button"
                  onClick={() => setIsGuestModalOpen(false)}
                  className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:text-lg ${CTA_FULL_WIDTH}`}
                  ariaLabel={t("booking_guest_names_done", "Done")}
                />
              </Modal.Footer>
            </Modal>
          </div>
        )}

        {/* Booking tags */}
        {filteredTags.length > 0 && (
          <fieldset className="gcw:w-full">
            <legend className="gcw:block gcw:text-body gcw:font-medium gcw:text-text-primary gcw:mb-2">
              {t("booking_tags")}
            </legend>
            <div className="gcw:flex gcw:flex-col gcw:gap-bs-3">
              {filteredTags.map((tag) => renderBookingTag(tag, bookingTagDeps))}
            </div>
          </fieldset>
        )}

        {/* Newsletter signup */}
        {showNewsletter && (
          <CheckboxInput
            id="newsletter-signup"
            name="newsletterSignup"
            label={
              <span className="gcw:text-body gcw:text-text-primary gcw:font-semibold">
                {t("booking_newsletter_signup")}
              </span>
            }
            register={register}
            className="gcw:w-full"
            inputClassName="gcw:rounded gcw:border-gray-300"
          />
        )}

        {/* Newsletter terms & conditions */}
        {showNewsletterTerms && (
          <p className="gcw:text-sm gcw:text-text-secondary gcw:pl-5">
            <Trans
              i18nKey="booking_newsletter_terms"
              components={{
                termsLink: (
                  <a
                    href="https://www.designmynight.com/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gcw:underline"
                    aria-label={t("booking_terms_link_label", "terms and conditions")}
                  >
                    {t("booking_terms_link_label", "terms and conditions")}
                  </a>
                ),
                privacyLink: (
                  <a
                    href="https://www.designmynight.com/privacy-notice"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gcw:underline"
                    aria-label={t("booking_privacy_link_label", "privacy notice")}
                  >
                    {t("booking_privacy_link_label", "privacy notice")}
                  </a>
                ),
              }}
            />
          </p>
        )}

        {/* Affiliate data permission */}
        {affiliate && source !== "partner" && (
          <CheckboxInput
            id="affiliate-data-permission"
            name="affiliateDataPermission"
            label={
              affiliate.data_permission_text ? (
                <span
                  className="gcw:text-body gcw:text-text-primary gcw:font-semibold gcw:[&_a]:text-primary gcw:[&_a]:underline gcw:[&_a]:cursor-pointer gcw:[&_a:hover]:text-primary-hover gcw:[&_a:focus-visible]:outline-2 gcw:[&_a:focus-visible]:outline-offset-2 gcw:[&_a:focus-visible]:outline-primary"
                  /* biome-ignore lint/security/noDangerouslySetInnerHtml: affiliate permission content must render as HTML and is sanitized with DOMPurify before display. */
                  dangerouslySetInnerHTML={{ __html: sanitizeConsentHtml(affiliate.data_permission_text) }}
                />
              ) : (
                <span className="gcw:text-body gcw:text-text-primary gcw:font-semibold">
                  Keep up to date with {affiliate.name}
                </span>
              )
            }
            register={register}
            className="gcw:w-full"
            inputClassName="gcw:rounded gcw:border-gray-300"
          />
        )}

        {/* In the widget the notice banners and offers sit just above the marketing preferences */}
        {isWidget && preorderPaymentBanner}
        {isWidget && pointsBanner}
        {isWidget && offersBlock}

        {/* Marketing preferences */}
        {filteredMarketingPrefs.length > 0 && (
          <fieldset className="gcw:w-full">
            <legend className="gcw:sr-only">{t("booking_marketing_preferences")}</legend>
            <div className="gcw:flex gcw:flex-col gcw:gap-2">
              {filteredMarketingPrefs.map((pref) => (
                <CheckboxInput
                  key={pref.id}
                  id={`marketing-pref-${pref.id}`}
                  name={`marketingPreferences.${pref.id}` as const}
                  label={
                    <span
                      className="gcw:text-body gcw:text-text-secondary gcw:[&_a]:text-primary gcw:[&_a]:underline gcw:[&_a]:cursor-pointer gcw:[&_a:hover]:text-primary-hover gcw:[&_a:focus-visible]:outline-2 gcw:[&_a:focus-visible]:outline-offset-2 gcw:[&_a:focus-visible]:outline-primary"
                      /* biome-ignore lint/security/noDangerouslySetInnerHtml: marketing preference content must render as HTML and is sanitized with DOMPurify before display. */
                      dangerouslySetInnerHTML={{ __html: sanitizeConsentHtml(pref.description) }}
                    />
                  }
                  register={register}
                  inputClassName="gcw:rounded gcw:border-gray-300"
                />
              ))}
            </div>
          </fieldset>
        )}

        {/* Booking Policy */}
        {showPolicy && (
          <div className="gcw:w-full gcw:rounded-md gcw:border gcw:border-gray-200 gcw:bg-bg-surface gcw:p-bs-3">
            <h3 className="gcw:text-xl gcw:font-heading gcw:font-bold gcw:text-text-primary gcw:mb-2">
              {t("booking_policy_title", "Booking Policy")}
            </h3>
            <div
              className="gcw:text-body gcw:text-text-policy gcw:mb-bs-3"
              /* biome-ignore lint/security/noDangerouslySetInnerHtml: booking policy content must render as HTML and is sanitized with DOMPurify before display. */
              dangerouslySetInnerHTML={{ __html: sanitizeConsentHtml(bookingOptions?.bookingPolicy ?? "") }}
            />
            <CheckboxInput
              id="policy-confirm"
              name="policyConfirm"
              label={
                <span className="gcw:text-body gcw:text-text-primary gcw:font-semibold">
                  {t("booking_policy_read_agree", "I have read and agree to the booking policy")}
                  <span className="gcw:text-text-primary"> *</span>
                </span>
              }
              register={register}
              validation={{ required: t("policy_required", "Please read the booking policy") }}
              error={errors.policyConfirm}
              inputClassName="gcw:rounded gcw:border-gray-300"
            />
          </div>
        )}

        {/* Action buttons — the notice stays above the button at every width,
            as the actions row is `flex-col-reverse` on small screens. */}
        <SlowRequestNotice />
        <div
          id="gc-customer-details-actions"
          className="gcw:flex gcw:flex-col-reverse gcw:@md:flex-row gcw:@md:gap-bs-3 gcw:mt-bs-3 widget-cta-sink"
        >
          {!submitHidden && (
            <PrimaryButton
              text={t(submitLabel.key, submitLabel.fallback)}
              type="submit"
              loading={submitGuard.isPending}
              className={`gcw:inline-flex gcw:items-center gcw:justify-center gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:text-lg ${CTA_FULL_WIDTH}`}
              ariaLabel={t(submitLabel.key, submitLabel.fallback)}
            />
          )}
        </div>
      </form>
    </section>
  );
};

export default CustomerDetailsForm;
